import { useCallback, useEffect, useRef, useState } from 'react';

export interface PlacePrediction {
  placeId: string;
  description: string;
  mainText: string;
  secondaryText: string;
}

export interface ResolvedPlace {
  placeId: string;
  description: string;
  formattedAddress: string;
  latitude: number;
  longitude: number;
  city?: string;
  state?: string;
  zipCode?: string;
}

/**
 * Our API's own Places proxy (`getAutocompletePredictions`, `geocodeAddress`),
 * used only when the browser cannot use Places: the script did not load, or
 * the Maps key refuses this site (a key restricted to other domains answers
 * REQUEST_DENIED).
 */
export interface PlacesFallback {
  predict: (input: string) => Promise<PlacePrediction[]>;
  resolve: (prediction: PlacePrediction) => Promise<ResolvedPlace | null>;
}

type PlacesLib = typeof google.maps.places;

/** The Places library from the script tag in `public/index.html`, once it has loaded. */
function placesLib(): PlacesLib | null {
  if (typeof window === 'undefined') return null;
  return window.google?.maps?.places ?? null;
}

/** Waits for the Places script; resolves null after `timeoutMs`. */
function waitForPlaces(timeoutMs = 10000): Promise<PlacesLib | null> {
  const ready = placesLib();
  if (ready) return Promise.resolve(ready);
  return new Promise(resolve => {
    const started = Date.now();
    const timer = setInterval(() => {
      const lib = placesLib();
      if (lib || Date.now() - started > timeoutMs) {
        clearInterval(timer);
        resolve(lib);
      }
    }, 100);
  });
}

const component = (place: google.maps.places.PlaceResult, type: string, short = false) => {
  const found = place.address_components?.find(c => c.types.includes(type));
  return (short ? found?.short_name : found?.long_name) || undefined;
};

/**
 * Google Places autocomplete straight from the browser, the way the car search
 * card does it (`addressInputSection`), instead of the backend round trip in
 * `addressInput`: one request per pause in typing, answers that arrive late are
 * dropped, and one session token covers the typing and the pick (Google bills
 * the session once).
 */
export function usePlacePredictions({
  debounceMs = 150,
  minLength = 2,
  types,
  countries,
  fallback,
}: {
  debounceMs?: number;
  minLength?: number;
  types?: string[];
  /** Spec 028: ISO codes ("ca") Google may answer from; none = anywhere, as before. */
  countries?: string[];
  fallback?: PlacesFallback;
} = {}) {
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(() => Boolean(placesLib()));
  const serviceRef = useRef<google.maps.places.AutocompleteService | null>(null);
  const tokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestRef = useRef(0);
  /** Set once the browser path failed (no script, key refused); later calls go to the fallback. */
  const deniedRef = useRef(false);
  const fallbackRef = useRef(fallback);
  fallbackRef.current = fallback;
  const typesKey = types?.join(',');
  const countriesKey = countries?.join(',');

  useEffect(() => {
    let alive = true;
    if (!ready)
      waitForPlaces().then(lib => {
        if (alive && lib) setReady(true);
      });
    return () => {
      alive = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // Only on mount: once the script is there it stays.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const token = () => {
    const lib = placesLib();
    if (!lib) return undefined;
    if (!tokenRef.current) tokenRef.current = new lib.AutocompleteSessionToken();
    return tokenRef.current;
  };

  const search = useCallback(
    (input: string) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      const text = input.trim();
      const request = ++requestRef.current;
      if (text.length < minLength) {
        setPredictions([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      timerRef.current = setTimeout(() => {
        const lib = placesLib();
        const viaFallback = () => {
          const backup = fallbackRef.current;
          if (!backup) {
            setPredictions([]);
            setLoading(false);
            return;
          }
          backup
            .predict(text)
            .then(results => {
              if (request !== requestRef.current) return;
              setPredictions(results);
              setLoading(false);
            })
            .catch(() => {
              if (request !== requestRef.current) return;
              setPredictions([]);
              setLoading(false);
            });
        };
        if (!lib || deniedRef.current) {
          viaFallback();
          return;
        }
        if (!serviceRef.current) serviceRef.current = new lib.AutocompleteService();
        serviceRef.current.getPlacePredictions(
          {
            input: text,
            sessionToken: token(),
            ...(typesKey ? { types: typesKey.split(',') } : {}),
            ...(countriesKey
              ? { componentRestrictions: { country: countriesKey.split(',') } }
              : {}),
          },
          (results, status) => {
            // A newer keystroke already asked again: this answer is stale.
            if (request !== requestRef.current) return;
            if (status === lib.PlacesServiceStatus.ZERO_RESULTS) {
              setPredictions([]);
              setLoading(false);
              return;
            }
            if (status !== lib.PlacesServiceStatus.OK || !results) {
              // REQUEST_DENIED and the like will not get better on the next keystroke.
              deniedRef.current = true;
              viaFallback();
              return;
            }
            setLoading(false);
            setPredictions(
              results.map(result => ({
                placeId: result.place_id,
                description: result.description,
                mainText: result.structured_formatting?.main_text ?? result.description,
                secondaryText: result.structured_formatting?.secondary_text ?? '',
              })),
            );
          },
        );
      }, debounceMs);
    },
    // token() only reads refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debounceMs, minLength, typesKey, countriesKey],
  );

  const clear = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    requestRef.current += 1;
    setPredictions([]);
    setLoading(false);
  }, []);

  /** Coordinates for a picked prediction; ends the billing session. */
  const resolve = useCallback((prediction: PlacePrediction): Promise<ResolvedPlace | null> => {
    const lib = placesLib();
    const backup = fallbackRef.current;
    if (!lib || deniedRef.current) {
      return backup ? backup.resolve(prediction).catch(() => null) : Promise.resolve(null);
    }
    return new Promise(done => {
      const service = new lib.PlacesService(document.createElement('div'));
      service.getDetails(
        {
          placeId: prediction.placeId,
          fields: ['geometry.location', 'formatted_address', 'address_components'],
          sessionToken: tokenRef.current ?? undefined,
        },
        (place, status) => {
          tokenRef.current = null;
          const location = place?.geometry?.location;
          if (status !== lib.PlacesServiceStatus.OK || !place || !location) {
            done(backup ? backup.resolve(prediction).catch(() => null) : null);
            return;
          }
          done({
            placeId: prediction.placeId,
            description: prediction.description,
            formattedAddress: place.formatted_address || prediction.description,
            latitude: location.lat(),
            longitude: location.lng(),
            city: component(place, 'locality') ?? component(place, 'postal_town'),
            state: component(place, 'administrative_area_level_1', true),
            zipCode: component(place, 'postal_code'),
          });
        },
      );
    });
  }, []);

  return { predictions, loading, ready, search, clear, resolve };
}
